export const ALL_ROOMS_PATTERN = /\ball\s+room(?:s|\s+types?)?\b/i;

// Wider than blackout's own `except|excluding` check, because CMs abbreviate.
const ROOM_EXCLUSION_PATTERN = /\b(?:except|excluding|excl|exc)\b/i;

const TRAILING_ORDINAL_PATTERN = /\s+\d+$/;
const LEADING_ORDINAL_PATTERN = /^\d+\s*-\s*/;
const NOISE_QUALIFIER_PATTERN = /\bonly\b|\(.*?\)/gi;
const TOKEN_PATTERN = /[a-z0-9]+/g;
const PLURAL_MIN_LENGTH = 4;

// Words that appear in these record names but carry no room-identifying signal on their own.
const GENERIC_WORDS = new Set([
  'room',
  'rooms',
  'type',
  'types',
  'only',
  'the',
  'a',
  'and',
  'all',
  'stop',
  'sell',
  'black',
  'out',
  'blackout',
  'archived',
]);

const NUMBER_WORD_TO_DIGIT: Record<string, string> = {
  one: '1',
  two: '2',
  three: '3',
  four: '4',
  five: '5',
  six: '6',
  seven: '7',
  eight: '8',
  nine: '9',
  ten: '10',
};

export interface RoomNameMatcherOptions {
  /** Treat "Tower" and "Towers" as the same token. */
  plurals?: boolean;
}

// Must run on the raw name: `stripRoomNameNoise` deletes the parentheses that usually hold the exclusion.
export function isAllRoomsName(rawName: string): boolean {
  return ALL_ROOMS_PATTERN.test(rawName) && !ROOM_EXCLUSION_PATTERN.test(rawName);
}

// Board basis is how these names carry the rate plan, since Salesforce has no field for it. Canonical
// labels, so two spellings of one basis do not read as two plans.
export type RoomNameRatePlanQualifier = 'All Inclusive' | 'Full Board' | 'Half Board' | 'Breakfast';

interface RatePlanQualifierRule {
  allowTrailing: boolean;
  pattern: RegExp;
  qualifier: RoomNameRatePlanQualifier;
}

// Breakfast alone does not count as a bare trailing phrase: "Studio with Breakfast" is a room name.
const RATE_PLAN_QUALIFIER_RULES: Array<RatePlanQualifierRule> = [
  { allowTrailing: true, pattern: /all\s+(?:meals?\s+)?inclusive|all\s+inc\b/i, qualifier: 'All Inclusive' },
  { allowTrailing: true, pattern: /full\s+board/i, qualifier: 'Full Board' },
  { allowTrailing: true, pattern: /half\s+board/i, qualifier: 'Half Board' },
  { allowTrailing: false, pattern: /(?:bed\s*(?:and|&)\s*)?breakfast/i, qualifier: 'Breakfast' },
];

const PARENTHETICAL_PATTERN = /\(([^)]*)\)/g;
const SEGMENT_DELIMITER_PATTERN = /[-–,]/;

// Only bracketed or after a delimiter, or a room whose own name contains the phrase would read as a plan.
function ratePlanQualifierSegments(rawName: string): Array<string> {
  return [
    ...Array.from(rawName.matchAll(PARENTHETICAL_PATTERN), (match) => match[1]),
    ...rawName.split(SEGMENT_DELIMITER_PATTERN).slice(1),
  ];
}

export interface RoomNameRatePlanMatch {
  qualifier: RoomNameRatePlanQualifier;
  /** Exactly what Salesforce wrote, so the CM is shown their own words. */
  text: string;
}

// Raw name, like `isAllRoomsName`: the qualifier is usually inside the brackets the noise stripper deletes.
export function detectRatePlanQualifier(rawName: string): RoomNameRatePlanMatch | null {
  const withoutParentheticals = rawName.replace(PARENTHETICAL_PATTERN, ' ').trim();

  for (const { allowTrailing, pattern, qualifier } of RATE_PLAN_QUALIFIER_RULES) {
    for (const segment of ratePlanQualifierSegments(rawName)) {
      const found = segment.match(pattern);

      if (found) {
        return { qualifier, text: found[0].trim() };
      }
    }

    if (allowTrailing) {
      const trailing = withoutParentheticals.match(new RegExp(`(?:${pattern.source})\\s*$`, 'i'));

      if (trailing) {
        return { qualifier, text: trailing[0].trim() };
      }
    }
  }

  return null;
}

export function stripRoomNameNoise(text: string): string {
  return text
    .replace(TRAILING_ORDINAL_PATTERN, '')
    .replace(LEADING_ORDINAL_PATTERN, '')
    .replace(NOISE_QUALIFIER_PATTERN, '')
    .trim();
}

function singularize(token: string): string {
  if (token.length >= PLURAL_MIN_LENGTH && token.endsWith('s') && !token.endsWith('ss')) {
    return token.slice(0, -1);
  }

  return token;
}

export function tokenize(text: string, options: RoomNameMatcherOptions = {}): Array<string> {
  const rawTokens = text.toLowerCase().match(TOKEN_PATTERN) ?? [];
  const tokens = rawTokens.map((token) => NUMBER_WORD_TO_DIGIT[token] ?? token);

  return options.plurals ? tokens.map(singularize) : tokens;
}

export function distinctiveTokens(text: string, options: RoomNameMatcherOptions = {}): Array<string> {
  return tokenize(text, options).filter((token) => !GENERIC_WORDS.has(token));
}

export function findMatchingRoomNames(
  candidateText: string,
  roomTypeNames: Array<string>,
  options: RoomNameMatcherOptions = {},
): Array<string> {
  const candidateTokens = distinctiveTokens(candidateText, options);

  if (!candidateTokens.length) {
    return [];
  }

  const candidateTokenSet = new Set(candidateTokens);
  const exactMatches = roomTypeNames.filter((roomTypeName) => {
    const roomTypeTokenSet = new Set(distinctiveTokens(roomTypeName, options));
    return (
      roomTypeTokenSet.size === candidateTokenSet.size && candidateTokens.every((token) => roomTypeTokenSet.has(token))
    );
  });

  if (exactMatches.length) {
    return exactMatches;
  }

  // No room name matches the candidate's words exactly — fall back to the room
  // names whose words are a superset (e.g. "2 Bedroom" candidate vs "2 Bedroom Apartment" room).
  return roomTypeNames.filter((roomTypeName) => {
    const roomTypeTokens = tokenize(roomTypeName, options);
    return candidateTokens.every((token) => roomTypeTokens.includes(token));
  });
}

export interface RoomNameSimilarity {
  roomTypeName: string;
  score: number;
}

// A score is only ever a prompt for a human, never a match.
export function findMostSimilarRoomName(
  candidateText: string,
  roomTypeNames: Array<string>,
  options: RoomNameMatcherOptions = {},
): RoomNameSimilarity | null {
  const candidateTokenSet = new Set(distinctiveTokens(candidateText, options));

  if (!candidateTokenSet.size) {
    return null;
  }

  let best: RoomNameSimilarity | null = null;

  for (const roomTypeName of roomTypeNames) {
    const roomTypeTokenSet = new Set(distinctiveTokens(roomTypeName, options));

    if (!roomTypeTokenSet.size) {
      continue;
    }

    const intersectionSize = [...candidateTokenSet].filter((token) => roomTypeTokenSet.has(token)).length;
    const unionSize = new Set([...candidateTokenSet, ...roomTypeTokenSet]).size;
    const score = intersectionSize / unionSize;

    if (score > (best?.score ?? 0)) {
      best = { roomTypeName, score };
    }
  }

  return best;
}
